import React from 'react';

import CloseIcon from '@mui/icons-material/Close';

import { MenuItem } from '@mui/material';

import { StyledSelect } from 'components/Input/styles';

import { UserFieldOption } from 'utils/api/modules/user/user.types';
import { RuleOperator } from 'utils/api/modules/memberGroups';

import RuleValueControl from './RuleValueControl';
import {
  ATTRIBUTE_LABELS,
  ATTRIBUTE_ORDER,
  conditionHasValue,
  isMultiValueOperator,
  isNoValueOperator,
  OPERATOR_LABELS,
  operatorsForAttribute,
  RuleConditionDraft,
} from './registry';
import { ConditionRemove, ConditionRow } from './styles';

export interface RuleConditionRowProps {
  condition: RuleConditionDraft;
  rowId: number;
  attributes?: string[];
  customFieldLabels: Record<string, string>;
  optionsByKind: Record<string, UserFieldOption[]>;
  onChange: (condition: RuleConditionDraft) => void;
  onRemove: () => void;
  canRemove: boolean;
  submitted: boolean;
}

const migrateValues = (
  values: RuleConditionDraft['values'],
  from: RuleOperator,
  to: RuleOperator
): RuleConditionDraft['values'] => {
  if (isNoValueOperator(to)) {
    return [];
  }
  if (isNoValueOperator(from)) {
    return [];
  }
  if (isMultiValueOperator(from) && !isMultiValueOperator(to)) {
    return values.slice(0, 1);
  }
  return values;
};

const labelForAttribute = (attribute: string, customFieldLabels: Record<string, string>): string =>
  ATTRIBUTE_LABELS[attribute] ?? customFieldLabels[attribute] ?? attribute;

const RuleConditionRow: React.FunctionComponent<RuleConditionRowProps> = ({
  condition,
  rowId,
  attributes = ATTRIBUTE_ORDER,
  customFieldLabels,
  optionsByKind,
  onChange,
  onRemove,
  canRemove,
  submitted,
}) => {
  const operators = operatorsForAttribute(condition.attribute);
  const rowError = submitted && !conditionHasValue(condition);

  const onAttributeChange = (attribute: string): void => {
    const nextOperators = operatorsForAttribute(attribute);
    const operator = nextOperators.includes(condition.operator) ? condition.operator : 'is';
    onChange({ attribute, operator, values: [] });
  };

  const onOperatorChange = (operator: RuleOperator): void => {
    onChange({ ...condition, operator, values: migrateValues(condition.values, condition.operator, operator) });
  };

  return (
    <ConditionRow data-testid={`rule-condition-${rowId}`}>
      <StyledSelect
        fullWidth
        value={condition.attribute}
        inputProps={{ 'aria-label': 'Field' }}
        onChange={(event) => onAttributeChange(event.target.value as string)}
      >
        {attributes.map((attribute) => (
          <MenuItem key={attribute} value={attribute}>
            {labelForAttribute(attribute, customFieldLabels)}
          </MenuItem>
        ))}
      </StyledSelect>

      <StyledSelect
        fullWidth
        value={condition.operator}
        inputProps={{ 'aria-label': 'Operator' }}
        onChange={(event) => onOperatorChange(event.target.value as RuleOperator)}
      >
        {operators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {OPERATOR_LABELS[operator]}
          </MenuItem>
        ))}
      </StyledSelect>

      <RuleValueControl
        attribute={condition.attribute}
        operator={condition.operator}
        values={condition.values}
        optionsByKind={optionsByKind}
        error={rowError}
        onChange={(values) => onChange({ ...condition, values })}
      />
      <ConditionRemove type="button" aria-label="Remove condition" onClick={onRemove} disabled={!canRemove}>
        <CloseIcon fontSize="small" />
      </ConditionRemove>
    </ConditionRow>
  );
};

export default RuleConditionRow;
